import React, { useState, useEffect } from 'react';
import './DailyCheckinPage.css';
import DailyRewardCard from '../components/DailyRewardCard';
import CheckinSuccessPopup from '../components/CheckinSuccessPopup';
import { checkinApi, CheckinClaimResponse } from '../services/api';
import { DailyCheckinStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { BaseLayout } from '@/components';

// Animation component for background - iOS optimized with preloader
const AnimatedBackground: React.FC = () => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [animationStarted, setAnimationStarted] = useState(false);
  
  useEffect(() => {
    const preloadAllImages = async () => {
      const imagePromises: Promise<void>[] = [];
      
      // Create promises for all 24 images
      for (let i = 1; i <= 24; i++) {
        const frameNumber = String(i).padStart(5, '0');
        const imageUrl = `/images/ani_checkin/Checkin_${frameNumber}.png`;
        
        const promise = new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            console.log(`Loaded frame ${i}/24`);
            resolve();
          };
          img.onerror = () => {
            console.error(`Failed to load frame ${i}: ${imageUrl}`);
            reject(new Error(`Failed to load image ${i}`));
          };
          img.src = imageUrl;
        });
        
        imagePromises.push(promise);
      }
      
      try {
        // Wait for ALL images to load
        await Promise.all(imagePromises);
        console.log('All 24 frames loaded successfully!');
        setImagesLoaded(true);
        
        // Start animation after a brief delay to ensure smooth start
        setTimeout(() => {
          setAnimationStarted(true);
        }, 100);
        
      } catch (error) {
        console.error('Failed to preload images:', error);
        // Still try to show animation even if some images fail
        setImagesLoaded(true);
        setAnimationStarted(true);
      }
    };
    
    preloadAllImages();
  }, []);
  
  useEffect(() => {
    let animationId: number;
    
    if (animationStarted && imagesLoaded) {
      let startTime = 0;
      const frameDuration = 80; // ~12 FPS for smoother animation on iOS
      
      const animate = (currentTime: number) => {
        if (startTime === 0) startTime = currentTime;
        const elapsed = currentTime - startTime;
        
        const frameIndex = Math.floor(elapsed / frameDuration) % 24;
        setCurrentFrame(frameIndex);
        
        animationId = requestAnimationFrame(animate);
      };
      
      animationId = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [animationStarted, imagesLoaded]);
  
  // Show loading state or first frame while images are loading
  if (!imagesLoaded) {
    return (
      <div className="animated-background-css">
        <div 
          className="frame active"
          style={{
            backgroundImage: `url(/images/ani_checkin/Checkin_00001.png)`,
            opacity: 0.5 // Dim while loading
          }}
        />
        {/* Optional: Add a loading indicator */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          fontSize: '12px',
          zIndex: 10
        }}>
          Loading...
        </div>
      </div>
    );
  }
  
  return (
    <div className="animated-background-css">
      {Array.from({ length: 24 }, (_, index) => {
        const frameNumber = String(index + 1).padStart(5, '0');
        return (
          <div
            key={index}
            className={`frame ${currentFrame === index ? 'active' : ''}`}
            style={{
              backgroundImage: `url(/images/ani_checkin/Checkin_${frameNumber}.png)`
            }}
          />
        );
      })}
    </div>
  );
};

interface DailyCheckinPageProps {
  onClose?: () => void;
}

const DailyCheckinPage: React.FC<DailyCheckinPageProps> = ({ onClose }) => {
  const { user, refreshUser } = useAuth();
  const [checkinStatus, setCheckinStatus] = useState<DailyCheckinStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  console.log('Today date:', today);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [claimedEnergyAmount, setClaimedEnergyAmount] = useState(0);
  const [claimedKilobaReward, setClaimedKilobaReward] = useState<{
    species: any;
    userKiloba: any;
  } | undefined>(undefined);

  // Load checkin status when component mounts
  useEffect(() => {
    const loadCheckinStatus = async () => {
      if (!user) {
        setError('User not authenticated');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Use JWT-protected API (no need to pass user ID)
        const response = await checkinApi.getWeeklyStatus();
        if (response.success && response.data) {
          setCheckinStatus(response.data as any);
        } else {
          setError('Failed to load checkin status');
        }
      } catch (err) {
        console.error('Error loading checkin status:', err);
        setError('Failed to load checkin status');
      } finally {
        setIsLoading(false);
      }
    };

    loadCheckinStatus();
  }, [user]); // Changed dependency to just user, not user.id

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    // Handle close modal
    if (onClose) {
      onClose();
    }
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    // DON'T close the main modal after success - let user continue
    // if (onClose) {
    //   onClose();
    // }
  };

  const handleClaim = async () => {
    if (!user || isClaiming) {
      return;
    }

    // Normal validation
    const canClaimAnyDay = checkinStatus?.checkinDays?.some(day => day.canClaim);
    if (!canClaimAnyDay) {
      return;
    }

    try {
      setIsClaiming(true);
      const response = await checkinApi.claimReward();
      
      if (response.success && response.data) {
        // Store the energy amount claimed for the success popup
        const energyAmount = response.data.data?.energyReward || 0;
        setClaimedEnergyAmount(energyAmount);
        
        // Store the Kiloba reward if it exists (day 7)
        if (response.data.data?.kilobaReward) {
          setClaimedKilobaReward(response.data.data.kilobaReward);
        } else {
          setClaimedKilobaReward(undefined);
        }
        
        // Reload checkin status after successful claim
        const statusResponse = await checkinApi.getWeeklyStatus();
        if (statusResponse.success && statusResponse.data) {
          setCheckinStatus(statusResponse.data as any);
        }
        
        // Refresh user data to sync energy changes
        await refreshUser();
        
        console.log(`Claimed reward successfully, energy: ${energyAmount}`, response.data.data?.kilobaReward ? `, kiloba: ${response.data.data.kilobaReward.species.name}` : '');
        
        // Show success popup
        setShowSuccessPopup(true);
      } else {
        setError('Failed to claim reward');
      }
    } catch (err) {
      console.error('Error claiming reward:', err);
      setError('Failed to claim reward');
    } finally {
      setIsClaiming(false);
    }
  };

  const getRewardIcon = (rewardType: 'energy' | 'special') => {
    if (rewardType === 'energy') {
      return '⚡';
    } else {
      return '🎁';
    }
  };

  if (isLoading) {
    return (
      <div className="daily-checkin-overlay">
        <div className="daily-checkin-modal">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !checkinStatus) {
    return (
      <div className="daily-checkin-overlay">
        <div className="daily-checkin-modal">
          <div>Error: {error || 'No checkin data available'}</div>
          <button onClick={handleClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div>
        <div className="daily-checkin-overlay no-select" onClick={handleOverlayClick}>
          <div style={{
            width: '100%',
            top: '5%',
            position: 'absolute',
          }}>
            <div 
              className="daily-checkin-modal" 
              onClick={(e) => e.stopPropagation()}
            >
              <AnimatedBackground />
              <button className="close-button touch-button" onClick={handleClose}>
                ✕
              </button>
              <div className='daily-checkin-title'>
                <span>Điểm danh hàng ngày</span>
              </div>

              <div className='daily-checkin-mindstone'>
                <div className='daily-checkin-mindstone-background'>
                  <div className='daily-checkin-mindstone-progress' style={{
                    width: `${(checkinStatus.completedDays / 30) * 100}%`
                  }}></div>
                  <div className='daily-checkin-mindstone-point'>
                    
                  </div>
                </div>
              </div>

              <div className="week-navigation">
                <span className="week-indicator">Tuần {checkinStatus.weekNumber}</span>
              </div>
              <div className="main-section">
                <div className="rewards-grid">
                    <div className="rewards-row top-row">
                        {checkinStatus.checkinDays.slice(0, 4).map((day) => (
                            <DailyRewardCard
                                key={day.day}
                                day={day.day}
                                type={day.isSpecial ? 'special' : 'energy'}
                                amount={day.energyReward}
                                claimed={day.checked}
                                available={day.canClaim}
                                isSpecial={day.isSpecial}
                                isToday={day.date.includes(today)}
                            />
                        ))}
                    </div>
                    <div className="rewards-row bottom-row">
                        {checkinStatus.checkinDays.slice(4, 6).map((day) => (
                            <DailyRewardCard
                                key={day.day}
                                day={day.day}
                                type={day.isSpecial ? 'special' : 'energy'}
                                amount={day.energyReward}
                                claimed={day.checked}
                                available={day.canClaim}
                                isSpecial={day.isSpecial}
                                isToday={day.date.includes(today)}
                            />
                        ))}
                        {checkinStatus.checkinDays.slice(6, 7).map((day) => (
                            <DailyRewardCard
                                key={day.day}
                                day={day.day}
                                type={day.isSpecial ? 'special' : 'energy'}
                                amount={day.energyReward}
                                claimed={day.checked}
                                available={day.canClaim}
                                isSpecial={day.isSpecial}
                                isToday={day.date.includes(today)}
                            />
                        ))}
                    </div>
                </div>
              </div>
            </div>
            
            <button 
              className={`claim-button touch-button ${!checkinStatus.checkinDays.some(day => day.canClaim) || isClaiming ? 'disabled' : ''}`}
              onClick={handleClaim}
              disabled={!checkinStatus.checkinDays.some(day => day.canClaim) || isClaiming}
            >
              {isClaiming 
                ? 'Đang điểm danh...' 
                : checkinStatus.checkinDays.some(day => day.canClaim) 
                  ? 'Điểm danh' 
                  : 'Đã điểm danh hôm nay'
              }
            </button>
          </div>
        </div>
        
        {showSuccessPopup && (
          <CheckinSuccessPopup 
            onClose={handleSuccessPopupClose}
            energyAmount={claimedEnergyAmount}
            kilobaReward={claimedKilobaReward}
          />
        )}
    </div>
  );
};

export default DailyCheckinPage;
